import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { NAV_ITEMS } from '../config/navItems';
import NavIcon from './NavIcon';
import SessionMenu from './SessionMenu';
import IaJobNotificationBar from './IaJobNotificationBar';
import IaFeedbackReviewHost from './IaFeedbackReviewHost';
import IaOnboardingReviewHost from './IaOnboardingReviewHost';
import WorkoutWizardHost from './WorkoutWizardHost';

const Layout = ({ children }) => {
  const { user, logout, isAuthenticated } = useAuth();
  const location = useLocation();
  const isLandingPage = location.pathname === '/' && !isAuthenticated;
  const isAuthPage =
    isLandingPage ||
    location.pathname === '/login' ||
    location.pathname === '/register' ||
    location.pathname === '/start' ||
    location.pathname.startsWith('/register/') ||
    location.pathname === '/terms' ||
    location.pathname === '/privacy';
  const isWorkoutPage = location.pathname === '/workout';
  const showGlobalChrome = !isAuthPage && !isWorkoutPage;
  const showBottomNav = isAuthenticated && !isAuthPage && (showGlobalChrome || isWorkoutPage);
  const showWorkoutDesktopNav = isAuthenticated && isWorkoutPage;

  const isActive = (path) => location.pathname === path;

  const navLinkClass = (path) =>
    `rounded-lg border px-3 py-1.5 font-mono text-[11px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-iron-accent/70 ${
      isActive(path)
        ? 'border-iron-accent bg-iron-accent text-black font-medium'
        : 'border-iron-border text-iron-muted hover:border-iron-accent hover:text-iron-accent'
    }`;

  return (
    <div className="min-h-screen bg-iron-bg text-iron-text flex flex-col">
      {isAuthenticated && !isAuthPage && <IaJobNotificationBar />}
      {isAuthenticated && !isAuthPage && <IaFeedbackReviewHost />}
      {isAuthenticated && !isAuthPage && <IaOnboardingReviewHost />}
      {isAuthenticated && !isAuthPage && <WorkoutWizardHost />}
      {showGlobalChrome && (
      <header className="sticky top-0 z-40 border-b border-iron-border bg-gradient-to-b from-[#0d0d10]/95 to-transparent backdrop-blur-xl">
        <div className="max-w-app mx-auto w-full px-5 pt-7 pb-4">
          <div className="flex justify-between items-start gap-3">
            <div>
              <Link to="/" className="font-display text-[2rem] leading-none tracking-[0.2em] text-cyan-300 drop-shadow-[0_0_12px_rgba(103,232,249,0.65)]">
                Ludi
              </Link>
              <span className="block font-mono text-xs text-iron-muted tracking-wide mt-1">
                Método · Dados · Propósito
              </span>
            </div>
            {isAuthenticated && <SessionMenu user={user} logout={logout} />}
          </div>

          {isAuthenticated && (
            <nav className="hidden sm:flex flex-wrap gap-2 mt-5">
              {NAV_ITEMS.map((item) => (
                <Link key={item.path} to={item.path} className={navLinkClass(item.path)}>
                  {item.label}
                </Link>
              ))}
            </nav>
          )}

          {!isAuthenticated && (
            <div className="flex gap-2 mt-5 justify-end">
              <Link
                to="/login"
                className="btn-primary px-5 py-2 rounded-lg font-mono text-xs"
              >
                Entrar
              </Link>
              <Link to="/register" className="btn-secondary px-5 py-2 rounded-lg font-mono text-xs">
                Cadastrar
              </Link>
            </div>
          )}
        </div>
      </header>
      )}

      {showWorkoutDesktopNav && (
        <nav className="sticky top-0 z-40 hidden border-b border-iron-border bg-iron-bg/95 backdrop-blur-xl sm:block">
          <div className="max-w-app mx-auto flex flex-wrap items-center gap-2 px-5 py-3">
            <Link
              to="/"
              className="mr-2 font-display text-lg tracking-[0.15em] text-cyan-300"
            >
              Ludi
            </Link>
            {NAV_ITEMS.map((item) => (
              <Link key={item.path} to={item.path} className={navLinkClass(item.path)}>
                {item.label}
              </Link>
            ))}
            <div className="ml-auto">
              <SessionMenu user={user} logout={logout} />
            </div>
          </div>
        </nav>
      )}

      {isWorkoutPage && isAuthenticated && (
        <header className="sticky top-0 z-40 border-b border-iron-border bg-iron-bg/95 backdrop-blur-xl sm:hidden">
          <div className="max-w-app mx-auto flex items-center justify-between gap-3 px-5 py-3">
            <Link to="/" className="font-display text-lg tracking-[0.15em] text-cyan-300">
              Ludi
            </Link>
            <SessionMenu user={user} logout={logout} />
          </div>
        </header>
      )}

      <main
        className={`flex-1 mx-auto w-full ${
          isLandingPage
            ? 'px-0 py-0'
            : showGlobalChrome
            ? 'max-w-app px-5 py-6 pb-safe-nav sm:pb-8'
            : isWorkoutPage
            ? 'max-w-app px-4 py-4 sm:px-5 sm:py-5 pb-safe-nav sm:pb-8'
            : 'max-w-app px-4 py-4 sm:px-5 sm:py-5'
        }`}
      >
        {children}
      </main>

      {showBottomNav && (
        <nav className="sm:hidden fixed bottom-0 left-0 right-0 z-50 border-t border-iron-border bg-iron-surface/95 backdrop-blur-md pb-[env(safe-area-inset-bottom,0px)]">
          <div className="max-w-app mx-auto flex justify-around py-2 px-2">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center justify-center gap-0.5 min-w-[2.75rem] min-h-[44px] rounded-lg px-1 py-1.5 font-mono text-[9px] uppercase tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-iron-accent/70 ${
                  isActive(item.path)
                    ? 'text-iron-accent bg-iron-accent/10'
                    : 'text-iron-muted hover:text-iron-accent'
                }`}
              >
                <NavIcon name={item.icon} className="h-5 w-5" />
                <span>{item.label}</span>
              </Link>
            ))}
          </div>
        </nav>
      )}

      {showGlobalChrome && (
      <footer className="border-t border-iron-border py-4 mt-auto hidden sm:block">
        <p className="text-center font-mono text-[10px] text-iron-muted tracking-wide">
          © {new Date().getFullYear()} Ludi
        </p>
      </footer>
      )}
    </div>
  );
};

export default Layout;
